import clsx from 'clsx'
import { ReactComponent as AutomaticSvg } from 'images/icons/cleaning.svg'
import { ReactComponent as EmergencySvg } from 'images/icons/emergency.svg'
import { ReactComponent as FirmwareUpdateSvg } from 'images/icons/firmware_update.svg'
import { ReactComponent as ManualSvg } from 'images/icons/joystick.svg'
import { ReactComponent as LocationSvg } from 'images/icons/location.svg'
import { ReactComponent as MappingSvg } from 'images/icons/map-bold.svg'
import { ReactComponent as MissionPlaySvg } from 'images/icons/mission_play.svg'
import { ReactComponent as PauseSvg } from 'images/icons/pause.svg'
import { ReactComponent as RoutingSvg } from 'images/icons/route.svg'
import { ReactComponent as IdleSvg } from 'images/icons/sand_clock.svg'
import { ReactComponent as UpdateSvg } from 'images/icons/update.svg'
import { ReactComponent as WashingSvg } from 'images/icons/washing.svg'
import { resolveCatalogOperationPresentation } from 'utils/catalogServicePresentation'
import { ROBOT_MODES, ROBOT_NETWORK_STATUSES, TASK_RUNTIME_STATE } from 'utils/constants'
import { IRobotInfo } from 'utils/interfaces'
import { getTaskRuntimeState } from 'utils/taskRuntimeState'
import { VENDOR } from 'utils/vendorDisplayName'

import style from './RobotModeIcons.module.css'

interface Props {
  robot: IRobotInfo
  large?: boolean
  hideEmergency?: boolean
  hidePause?: boolean
}

const RobotModeIcons = ({ robot, large, hideEmergency, hidePause }: Props) => {
  const isOnline = robot.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE
  if (!isOnline) return null

  const activeMode = robot.activeMode
  const taskRuntimeState = getTaskRuntimeState(robot)
  const isGausium = robot.vendor === VENDOR.GAUSIUM
  const isAsbis = robot.vendor === VENDOR.ASBIS
  const workingModeIcon = isAsbis ? MissionPlaySvg : AutomaticSvg
  const servicePointPresentation = resolveCatalogOperationPresentation(robot)

  const renderModeIcon = () => {
    let Icon = null
    let title = ''

    if (!isGausium && !isAsbis && servicePointPresentation) {
      Icon = servicePointPresentation.Icon
      title = servicePointPresentation.iconTitle
    } else if (isAsbis) {
      if (activeMode === ROBOT_MODES.MODE_AUTOMATIC) {
        Icon = workingModeIcon
        title = 'Working Mode'
      } else {
        Icon = IdleSvg
        title = 'Waiting for a task'
      }
    } else if (isGausium) {
      if (taskRuntimeState === TASK_RUNTIME_STATE.RUNNING || taskRuntimeState === TASK_RUNTIME_STATE.PAUSED) {
        Icon = AutomaticSvg
        title =
          taskRuntimeState === TASK_RUNTIME_STATE.PAUSED
            ? `Paused task${robot.activeTask?.missionName ? `\nMission: ${robot.activeTask.missionName}` : ''}`
            : `Working Mode${robot.activeTask?.missionName ? `\nMission: ${robot.activeTask.missionName}` : ''}`
      } else {
        Icon = IdleSvg
        title = 'Waiting for a task'
      }
    } else
      switch (activeMode) {
        case ROBOT_MODES.MODE_NOT_SET:
          Icon = IdleSvg
          title = 'Waiting for a task'
          break
        case ROBOT_MODES.MODE_MANUAL:
          Icon = ManualSvg
          title = 'Manual Mode'
          break
        case ROBOT_MODES.MODE_AUTOMATIC:
          Icon = AutomaticSvg
          title =
            taskRuntimeState === TASK_RUNTIME_STATE.PAUSED
              ? `Paused task${robot.activeTask?.missionName ? `\nMission: ${robot.activeTask.missionName}` : ''}`
              : `Working Mode${robot.activeTask?.missionName ? `\nMission: ${robot.activeTask.missionName}` : ''}`
          break
        case ROBOT_MODES.MODE_MAPPING:
          Icon = MappingSvg
          title = 'Mapping'
          break
        case ROBOT_MODES.MODE_ROUTE_RECORDING:
          Icon = RoutingSvg
          title = 'Route Recording'
          break
        case ROBOT_MODES.MODE_CALIBRATION_SERVICE_POINT:
          Icon = servicePointPresentation?.Icon ?? WashingSvg
          title = servicePointPresentation?.iconTitle ?? 'Calibration'
          break
        case ROBOT_MODES.MODE_RESET_CALIBRATION_SERVICE_POINT:
          Icon = servicePointPresentation?.Icon ?? WashingSvg
          title = servicePointPresentation?.iconTitle ?? 'Resetting Calibration'
          break
        case ROBOT_MODES.MODE_SERVICE_POINT:
          Icon = servicePointPresentation?.Icon ?? WashingSvg
          title = servicePointPresentation?.iconTitle ?? 'Service point action'
          break
        case ROBOT_MODES.MODE_UPDATING_FIRMWARE:
          Icon = FirmwareUpdateSvg
          title = 'Firmware Updating'
          break
        case ROBOT_MODES.MODE_REBOOT_ROBOT:
          Icon = UpdateSvg
          title = 'Robot Rebooting'
          break
        case ROBOT_MODES.MODE_SWITCHING_TO_LIDAR_LOCALIZATION:
        case ROBOT_MODES.MODE_SWITCHING_TO_GNSS_BASED_LOCALIZATION:
        case ROBOT_MODES.MODE_SWITCHING_TO_COMBINED_LOCALIZATION:
          Icon = LocationSvg
          title = 'Switching localization mode'
          break
        default:
          return null
      }

    if (!Icon) return null

    return (
      <div className={style.statusItem} title={title}>
        <Icon
          className={clsx(
            style.modeIcon,
            large && style.largeIcon,
            Icon === IdleSvg && style.sandClockIcon,
            large && Icon === IdleSvg && style.largeSandClockIcon
          )}
        />
      </div>
    )
  }

  const renderCommandIcon = () => {
    if (!hideEmergency && robot.emergencyButton) {
      return (
        <div className={style.statusItem} title="Emergency Stop">
          <EmergencySvg className={clsx(style.statusIcon, style.emergencyIcon)} />
        </div>
      )
    }

    if (!hidePause && taskRuntimeState === TASK_RUNTIME_STATE.PAUSED) {
      return (
        <div className={style.statusItem} title="Paused">
          <PauseSvg className={style.statusIcon} style={{ color: '#4680fdff' }} />
        </div>
      )
    }

    return null
  }

  return (
    <div className={clsx(style.statusContainer, large && style.largeContainer)}>
      {renderModeIcon()}
      {renderCommandIcon()}
    </div>
  )
}

export default RobotModeIcons
